import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CountContext from '../../context/countContext';
import useBooking from '../../hooks/useBooking';
import { blockerReason } from '../../util/blockerUtil';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending') return 'offen';
  if (s === 'approved') return 'freigegeben';
  if (s === 'rejected') return 'abgelehnt';
  return s || '-';
}

function statusChip(status) {
  const s = String(status || '');
  if (s === 'pending') return <Chip size='small' label={statusLabel(s)} color='warning' />;
  if (s === 'approved') return <Chip size='small' label={statusLabel(s)} color='success' />;
  if (s === 'rejected') return <Chip size='small' label={statusLabel(s)} color='default' />;
  return <Chip size='small' label={statusLabel(s)} />;
}

function formatUser(user) {
  if (!user) return '-';
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : user.username || '-';
}

function branchName(branch) {
  if (!branch || typeof branch !== 'object') return '-';
  return branch.longName || branch.locationName || '-';
}

function entertainmentName(doc) {
  const ent = doc?.entertainmentId;
  if (ent && typeof ent === 'object') {
    return ent.longName || ent.entertainmentName || doc.entertainment || '-';
  }
  return doc?.entertainment || '-';
}

function resourceNamesFromRequest(request) {
  const ids = request?.resourceIds;
  if (!Array.isArray(ids) || ids.length === 0) return 'Alle Räume';
  const names = ids.map(r => (r && typeof r === 'object' ? r.name : null)).filter(Boolean);
  return names.length ? names.join(', ') : 'Alle Räume';
}

const DATE_FMT = 'DD.MM.YYYY HH:mm';

function formatRange(start, end) {
  if (!start || !end) return '-';
  const s = dayjs(start);
  const e = dayjs(end);
  if (!s.isValid() || !e.isValid()) return '-';
  if (s.isSame(e, 'day')) {
    return `${s.format(DATE_FMT)} – ${e.format('HH:mm')}`;
  }
  return `${s.format(DATE_FMT)} – ${e.format(DATE_FMT)}`;
}

// Human readable duration between two timestamps.
function formatDuration(start, end) {
  if (!start || !end) return '';
  const ms = dayjs(end).valueOf() - dayjs(start).valueOf();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const totalMinutes = Math.round(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (days) parts.push(`${days} T`);
  if (hours) parts.push(`${hours} Std`);
  if (minutes) parts.push(`${minutes} Min`);
  return parts.join(' ') || '0 Min';
}

// Blockers store the reason inside a prefixed comment; show the reason cleanly.
// A blocker created without one carries only boilerplate, which shows as "-".
function blockerComment(comment) {
  return blockerReason(comment) || '-';
}

// Keeps the action column reachable while the table scrolls sideways — the
// delete button must never be the column that disappears off the right edge.
const stickyActionSx = theme => ({
  position: 'sticky',
  right: 0,
  zIndex: 2,
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
});

function BlockerManagement() {
  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const {
    getBlockerRequests,
    approveBlockerRequest,
    rejectBlockerRequest,
    getBlockers,
    deleteBlocker,
  } = useBooking();

  const [status, setStatus] = useState('pending');
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [requests, setRequests] = useState([]);

  const [isLoadingBlockers, setIsLoadingBlockers] = useState(true);
  const [blockers, setBlockers] = useState([]);

  const [busyIds, setBusyIds] = useState(() => new Set());

  const [rejectDialogRequest, setRejectDialogRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);

  const busySet = useMemo(() => busyIds, [busyIds]);

  const markBusy = (id, next) => {
    setBusyIds(prev => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const loadRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const res = await getBlockerRequests(status);
      setRequests(Array.isArray(res?.results) ? res.results : []);
    } catch (error) {
      toast.error('Fehler beim Laden der Blocker-Anfragen');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const loadBlockers = async () => {
    setIsLoadingBlockers(true);
    try {
      const res = await getBlockers();
      setBlockers(Array.isArray(res?.results) ? res.results : []);
    } catch (error) {
      toast.error('Fehler beim Laden der Blocker-Übersicht');
    } finally {
      setIsLoadingBlockers(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    loadBlockers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshBadge = () => countCtx?.refreshCount?.('blockerRequests');

  const handleApprove = async requestId => {
    markBusy(requestId, true);
    try {
      await approveBlockerRequest(requestId);
      toast.success('Blocker-Anfrage freigegeben');
      await Promise.all([loadRequests(), loadBlockers()]);
      refreshBadge();
    } catch (error) {
      toast.error('Fehler beim Freigeben der Blocker-Anfrage');
    } finally {
      markBusy(requestId, false);
    }
  };

  const openRejectDialog = request => {
    setRejectDialogRequest(request);
    setRejectReason('');
  };

  const closeRejectDialog = () => {
    setRejectDialogRequest(null);
    setRejectReason('');
  };

  const submitReject = async () => {
    const requestId = rejectDialogRequest?._id;
    if (!requestId) return;
    markBusy(requestId, true);
    try {
      await rejectBlockerRequest(requestId, rejectReason.trim());
      toast.success('Blocker-Anfrage abgelehnt');
      closeRejectDialog();
      await loadRequests();
      refreshBadge();
    } catch (error) {
      toast.error('Fehler beim Ablehnen der Blocker-Anfrage');
    } finally {
      markBusy(requestId, false);
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?._id;
    if (!id) return;
    markBusy(id, true);
    try {
      await deleteBlocker(id);
      toast.success('Blocker gelöscht');
      setDeleteTarget(null);
      await loadBlockers();
    } catch (error) {
      toast.error('Fehler beim Löschen des Blockers');
    } finally {
      markBusy(id, false);
    }
  };

  // Either the company-wide right or the one scoped to the assigned branches;
  // the backend limits what an own-scoped user sees and may act on.
  if (
    !authCtx.checkRight('internalBooking:special:blockDirect') &&
    !authCtx.checkRight('internalBooking:special:blockDirectOwn')
  ) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BlockIcon color='warning' />
        <Typography variant='h5' fontWeight='bold'>
          Blocker-Verwaltung
        </Typography>
      </Box>

      {/* ── Section 1: blocker requests ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant='h6' fontWeight='bold'>
          Blocker-Anfragen
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label='Status' onChange={e => setStatus(e.target.value)}>
              <MenuItem value='pending'>offen</MenuItem>
              <MenuItem value='approved'>freigegeben</MenuItem>
              <MenuItem value='rejected'>abgelehnt</MenuItem>
              <MenuItem value=''>alle</MenuItem>
            </Select>
          </FormControl>
          <Button variant='outlined' onClick={loadRequests} disabled={isLoadingRequests}>
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {isLoadingRequests ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ mb: 5 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Angefragt am</TableCell>
                <TableCell>Filiale</TableCell>
                <TableCell>Entertainment</TableCell>
                <TableCell>Räume</TableCell>
                <TableCell>Zeitraum</TableCell>
                <TableCell>Grund</TableCell>
                <TableCell>Angefragt von</TableCell>
                <TableCell align='right' sx={stickyActionSx}>
                  Aktion
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color='text.secondary'>Keine Blocker-Anfragen.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map(r => {
                  const id = r._id;
                  const isBusy = busySet.has(id);
                  const requestedAt = r.meta?.requestedAt
                    ? dayjs(r.meta.requestedAt).format(DATE_FMT)
                    : '-';
                  const reason = r.reason || '-';
                  const decisionComment = r.decisionComment || '';
                  return (
                    <TableRow key={id}>
                      <TableCell>{statusChip(r.status)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{requestedAt}</TableCell>
                      <TableCell>{branchName(r.branchId)}</TableCell>
                      <TableCell>{entertainmentName(r)}</TableCell>
                      <TableCell>{resourceNamesFromRequest(r)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatRange(r.startTime, r.endTime)}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={
                          r.status === 'rejected' && decisionComment
                            ? `Grund: ${reason}\nAblehnung: ${decisionComment}`
                            : reason
                        }
                      >
                        {r.status === 'rejected' && decisionComment
                          ? `${reason} · Ablehnung: ${decisionComment}`
                          : reason}
                      </TableCell>
                      <TableCell>{formatUser(r.meta?.requestedBy)}</TableCell>
                      <TableCell align='right' sx={stickyActionSx}>
                        {r.status === 'pending' ? (
                          <Box sx={{ display: 'inline-flex', gap: 1 }}>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => handleApprove(id)}
                              disabled={isBusy}
                            >
                              Freigeben
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              color='error'
                              onClick={() => openRejectDialog(r)}
                              disabled={isBusy}
                            >
                              Ablehnen
                            </Button>
                          </Box>
                        ) : (
                          <Typography color='text.secondary'>-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Section 2: existing blockers overview ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon color='action' />
          <Typography variant='h6' fontWeight='bold'>
            Bestehende Blocker
          </Typography>
          <Chip size='small' label={blockers.length} sx={{ ml: 0.5 }} />
        </Box>
        <Button variant='outlined' onClick={loadBlockers} disabled={isLoadingBlockers}>
          Aktualisieren
        </Button>
      </Box>

      {isLoadingBlockers ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Entertainment</TableCell>
                <TableCell>Filiale</TableCell>
                <TableCell>Raum</TableCell>
                <TableCell>Zeitraum</TableCell>
                <TableCell>Dauer</TableCell>
                <TableCell>Grund</TableCell>
                <TableCell>Ersteller</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell align='right' sx={stickyActionSx}>
                  Aktion
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color='text.secondary'>Keine bestehenden Blocker.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                blockers.map(b => {
                  const id = b._id;
                  const isBusy = busySet.has(id);
                  const room =
                    b.resourceId && typeof b.resourceId === 'object'
                      ? b.resourceId.name
                      : 'Alle Räume';
                  const createdAt = b.meta?.createdAt
                    ? dayjs(b.meta.createdAt).format(DATE_FMT)
                    : '-';
                  const comment = blockerComment(b.comment);
                  return (
                    <TableRow key={id} hover>
                      <TableCell>{entertainmentName(b)}</TableCell>
                      <TableCell>{branchName(b.branchId)}</TableCell>
                      <TableCell>{room || 'Alle Räume'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatRange(b.startTime, b.endTime)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDuration(b.startTime, b.endTime)}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 260,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={comment}
                      >
                        {comment}
                      </TableCell>
                      <TableCell>{formatUser(b.createdByUser)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{createdAt}</TableCell>
                      <TableCell align='right' sx={stickyActionSx}>
                        <Tooltip title='Blocker löschen'>
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => setDeleteTarget(b)}
                              disabled={isBusy}
                              aria-label='delete'
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject dialog */}
      <Dialog open={Boolean(rejectDialogRequest)} onClose={closeRejectDialog} maxWidth='sm' fullWidth>
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Blocker-Anfrage ablehnen
            </Typography>
            <IconButton edge='end' color='inherit' onClick={closeRejectDialog}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Der Ablehnungsgrund wird dem Antragsteller angezeigt.
          </Typography>
          <TextField
            label='Ablehnungsgrund'
            placeholder='z.B. Defekt zuerst mit Technik prüfen'
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='text' onClick={closeRejectDialog}>
              Abbrechen
            </Button>
            <Button variant='contained' color='error' onClick={submitReject}>
              Ablehnen
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Blocker löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll der Blocker für <strong>{entertainmentName(deleteTarget || {})}</strong>
            {deleteTarget ? ` (${formatRange(deleteTarget.startTime, deleteTarget.endTime)})` : ''}{' '}
            wirklich gelöscht werden? Der Zeitraum wird danach wieder buchbar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button
            color='error'
            variant='contained'
            onClick={confirmDelete}
            disabled={deleteTarget ? busySet.has(deleteTarget._id) : false}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BlockerManagement;
